import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import { useParams } from 'react-router-dom'

import { useGetMediaUrls } from '../../hooks/useGetMediaUrls'

import MediaGridReport from '../MediaGridReport/MediaGridReport'

/**
 * One report section, fetching its own photos.
 *
 * The sections that came before take theirs as props, threaded down from
 * CustomerReport. That is workable for a handful. The security side adds
 * fourteen at once and `useGetMediaUrls` needs nothing but the customer and the
 * kind, so these fetch for themselves rather than adding fourteen more props to
 * two files. The lists below are module constants, so the hook order is fixed.
 */
const MediaKindSection = React.memo<{
  company: string
  customerID: string
  mediaKind: c.MediaKind
  title: string
}>(function MediaKindSection({ company, customerID, mediaKind, title }) {
  const items = useGetMediaUrls(
    'images-videos',
    customerID,
    mediaKind,
  ) as c.MediaData[]

  return (
    <MediaGridReport
      company={company}
      items={items}
      kicker={c.mediaKindToHeadingAbove[mediaKind]}
      title={title}
    />
  )
})

/**
 * The Smart Home survey photos, titled exactly as `mediaToLabel` titles them so
 * the report calls a section what the rep who shot it calls it.
 *
 * The two post-installation kinds are here on purpose. They were first left
 * out on the reasoning that photos taken after the job belong to the
 * certificate of completion - but Jaron was already told, in writing on Aug 22,
 * that 'Camera Locations, Doorbell Locations, 360 House View, Post Installation'
 * all fail to reach the report, listed as a defect. Excluding them would have
 * quietly walked back something he had already been promised.
 *
 * Left out, and the only one: `cancellation_letter`. It is a cancellation
 * document rather than a survey photo and it was never part of that list, so it
 * has no business in a customer's sales report. That one still needs Jaron to
 * say where it goes.
 */
const SMART_HOME_SECTIONS = [
  ['security_camera_locations', 'Camera Locations'],
  // 12896840378, Jaron 2026-09-07: "Keep cameras" + "Add S4/S3/S2" - the
  // original row stays, these three are new and empty until reps upload.
  ['security_camera_locations_s4', 'S4 Camera locations'],
  ['security_camera_locations_s3', 'S3 Camera locations'],
  ['security_camera_locations_s2', 'S2 Camera locations'],
  // 13019073213, Jaron 2026-09-13: confirmed placement alongside S4/S3/S2.
  ['security_camera_locations_indoor', 'Indoor Camera locations'],
  ['security_doorbell_locations', 'Doorbell Locations'],
  ['security_360_house_view', '360 House View'],
  ['security_attic', 'Attic Access'],
  ['security_router_location', 'Router Location'],
  ['security_post_camera_installation', 'Post Camera Installation'],
  // Label renamed from 'Post Alarm Installation' (Monday 12824335937) and still
  // pending Jaron's OK on the wording; it follows mediaToLabel either way.
  ['security_post_alarm_installation', 'Post Panel Installation'],
] as const

/** The eight CCTV angles, numbered from 1 as the app numbers them. */
const CAM_ANGLE_SECTIONS = Array.from(
  { length: 8 },
  (_, i) => [`security_cam${i}`, `Cam angle #${i + 1}`] as const,
)

export type MediaImagePagesProps = c.r<{
  houseViewPictures: c.MediaData[]

  // Assessment
  atticPictures: c.MediaData[]
  roofPictures: c.MediaData[]
  windowPictures: c.MediaData[]
  hvacPictures: c.MediaData[]
  otherAreasPictures: c.MediaData[]

  // Documentation
  idPictures: c.MediaData[]
  otherDocumentsPictures: c.MediaData[]

  // Solar survey
  thermalPictures: c.MediaData[]
  solarProposalPictures: c.MediaData[]
  electricityPictures: c.MediaData[]
  allExteriorWallsPics: c.MediaData[]
  raftersPics: c.MediaData[]
  allPlanesRoofPics: c.MediaData[]
  electricalPictures: c.MediaData[]
  otherSolarSurveyPics: c.MediaData[]
  adjustmentPictures: c.MediaData[]
  electricalOldPictures: c.MediaData[]

  // Roof claims
  rcFrontHousePictures: c.MediaData[]
  rcAddressVerificationPictures: c.MediaData[]
  rc3DViewPictures: c.MediaData[]
  rcSolarPanelsPictures: c.MediaData[]
  rcGaragePics: c.MediaData[]
  rcWiresHomePictures: c.MediaData[]
  rcSlidingPictures: c.MediaData[]
  rcLayersPictures: c.MediaData[]
  rcInclinationPictures: c.MediaData[]
  rcRoofOverviewPictures: c.MediaData[]
  rcDripEdgePictures: c.MediaData[]
  rcGuttersPictures: c.MediaData[]
  rcFlashingsPictures: c.MediaData[]
  rcPenetrationsPictures: c.MediaData[]
  rcHailPictures: c.MediaData[]
  rcPropertyDamagesPics: c.MediaData[]
  rcWindDamagesPics: c.MediaData[]
  rcShingleLengthPics: c.MediaData[]
  rcShinglesColorPics: c.MediaData[]

  // The shared roof claim gate (`c.roofClaimVisible`, 13376571808). The app
  // shows its Roof Claim photo rows on this same rule, so a photo a rep files
  // there always reaches these pages. Optional: older callers keep the
  // product-only rule below unchanged.
  showRoofClaim?: boolean
  // Roof Claim unchecked in the report menu (13411491362): its photos go too,
  // on screen and in the PDF. Wins over the rules above; local view only.
  roofClaimExcluded?: boolean
  // Remote link with sections (13411491362): only the Roof Claim photos when
  // that was sent, never Id Pictures or the other documents.
  onlyRoofClaim?: boolean
}>

// Roof claim page title = the app row label (single source, 13386495658).
const rcTitle = (kind: c.MediaKind): string =>
  c.mediaToLabelRoofClaims[kind] ?? kind

export default React.memo<MediaImagePagesProps>(function MediaImagePages({
  houseViewPictures,

  // Assessment
  atticPictures,
  roofPictures,
  windowPictures,
  hvacPictures,
  otherAreasPictures,

  // Documentation
  idPictures,
  otherDocumentsPictures,

  // Solar Survey
  thermalPictures,
  solarProposalPictures,
  electricityPictures,
  allExteriorWallsPics,
  raftersPics,
  allPlanesRoofPics,
  electricalPictures,
  otherSolarSurveyPics,
  adjustmentPictures,
  electricalOldPictures,

  // Roof claims
  rcFrontHousePictures,
  rcAddressVerificationPictures,
  rc3DViewPictures,
  rcSolarPanelsPictures,
  rcGaragePics,
  rcWiresHomePictures,
  rcSlidingPictures,
  rcLayersPictures,
  rcInclinationPictures,
  rcRoofOverviewPictures,
  rcDripEdgePictures,
  rcGuttersPictures,
  rcFlashingsPictures,
  rcPenetrationsPictures,
  rcHailPictures,
  rcPropertyDamagesPics,
  rcWindDamagesPics,
  rcShingleLengthPics,
  rcShinglesColorPics,
  showRoofClaim = false,
  roofClaimExcluded = false,
  onlyRoofClaim = false,
}) {
  const { customerID } = useParams<'customerID'>()
  const { currProducts } = r.useProducts(customerID!)
  const roofClaimPages =
    !roofClaimExcluded &&
    (showRoofClaim || currProducts.includes('RC') || currProducts.length === 0)
  const [company] = r.useCustomerField(customerID!, 'solarCompany')

  return (
    <>
      {!onlyRoofClaim && (
        <>
          <MediaGridReport
            company={company}
            items={houseViewPictures}
            title="House View From Street"
          />

          {/* Assessment */}
          <MediaGridReport
            company={company}
            items={atticPictures}
            title="Attic Pictures"
          />
          <MediaGridReport
            company={company}
            items={roofPictures}
            title="Roof Pictures"
          />
          <MediaGridReport
            company={company}
            items={windowPictures}
            title="Windows Pictures"
          />
          <MediaGridReport
            company={company}
            items={hvacPictures}
            title="HVAC Pictures"
          />
          <MediaKindSection
            company={company}
            customerID={customerID!}
            mediaKind="mini_split_surroundings"
            title="Mini split surroundings (wall & floor)"
          />
          <MediaGridReport
            company={company}
            items={otherAreasPictures}
            title="Other Areas"
          />

          {/* Documentation */}
          <MediaGridReport
            company={company}
            items={idPictures}
            title="Id Pictures"
          />
          <MediaGridReport
            company={company}
            items={otherDocumentsPictures}
            title="Other Documents"
          />

          {/* Smart Home. Not gated on the customer having Security selected, unlike
          the Solar block below. MediaGridReport already renders nothing for an
          empty list, so a product gate would add no hiding of its own - it would
          only ADD a way for photos that exist to become unreachable, which is
          the exact defect that forced the Aug 14 revert of the section-hiding
          work. A section here appears if and only if there are photos in it. */}
          {SMART_HOME_SECTIONS.map(([mediaKind, title]) => (
            <MediaKindSection
              company={company}
              customerID={customerID!}
              key={mediaKind}
              mediaKind={mediaKind}
              title={title}
            />
          ))}

          {CAM_ANGLE_SECTIONS.map(([mediaKind, title]) => (
            <MediaKindSection
              company={company}
              customerID={customerID!}
              key={mediaKind}
              mediaKind={mediaKind as c.MediaKind}
              title={title}
            />
          ))}

          {/* Solar Survey */}
          {(currProducts.includes('Solar') || currProducts.length === 0) && (
            <>
              <MediaGridReport
                company={company}
                items={thermalPictures}
                title="Thermal Pictures"
              />
              <MediaGridReport
                company={company}
                items={solarProposalPictures}
                title="Solar Proposal"
              />
              <MediaGridReport
                company={company}
                items={electricityPictures}
                title="Electricity Bill"
              />
              <MediaGridReport
                company={company}
                items={allExteriorWallsPics}
                title="All Exterior Walls"
              />
              <MediaGridReport
                company={company}
                items={raftersPics}
                title="Rafters"
              />
              <MediaGridReport
                company={company}
                items={allPlanesRoofPics}
                title="All planes roof"
              />
              <MediaGridReport
                company={company}
                items={electricalPictures}
                title="Electrical Panel"
              />
              <MediaGridReport
                company={company}
                items={otherSolarSurveyPics}
                title="Other (Site Survey)"
              />
              <MediaGridReport
                company={company}
                items={adjustmentPictures}
                title="Adjustment"
              />
              <MediaGridReport
                company={company}
                items={electricalOldPictures}
                title="Electrical Panel"
              />
            </>
          )}
        </>
      )}

      {/* Titles come from the shared row labels (c.mediaToLabelRoofClaims),
          so the printed pages read exactly like the app's Roof Claim rows.
          rc_ice_shield is retired (13386495658): stored photos stay in
          Firebase, this report just no longer prints them. */}
      {roofClaimPages && (
        <>
          <MediaGridReport
            company={company}
            items={rcFrontHousePictures}
            title={rcTitle('rc_front_house')}
          />
          <MediaGridReport
            company={company}
            items={rcAddressVerificationPictures}
            title={rcTitle('rc_address_verification')}
          />
          <MediaGridReport
            company={company}
            items={rc3DViewPictures}
            title={rcTitle('rc_3d_view')}
          />
          <MediaGridReport
            company={company}
            items={rcSolarPanelsPictures}
            title={rcTitle('rc_solar_panels')}
          />
          <MediaGridReport
            company={company}
            items={rcGaragePics}
            title={rcTitle('rc_garage')}
          />
          <MediaGridReport
            company={company}
            items={rcWiresHomePictures}
            title={rcTitle('rc_wires_home')}
          />
          <MediaGridReport
            company={company}
            items={rcSlidingPictures}
            title={rcTitle('rc_siding')}
          />
          <MediaGridReport
            company={company}
            items={rcLayersPictures}
            title={rcTitle('rc_layers')}
          />
          <MediaGridReport
            company={company}
            items={rcInclinationPictures}
            title={rcTitle('rc_roof_inclination')}
          />
          <MediaGridReport
            company={company}
            items={rcRoofOverviewPictures}
            title={rcTitle('rc_roof_overview')}
          />
          <MediaGridReport
            company={company}
            items={rcDripEdgePictures}
            title={rcTitle('rc_drip_edge')}
          />
          <MediaGridReport
            company={company}
            items={rcGuttersPictures}
            title={rcTitle('rc_gutters')}
          />
          <MediaGridReport
            company={company}
            items={rcFlashingsPictures}
            title={rcTitle('rc_flashings')}
          />
          <MediaGridReport
            company={company}
            items={rcPenetrationsPictures}
            title={rcTitle('rc_penetrations')}
          />
          <MediaGridReport
            company={company}
            items={rcHailPictures}
            title={rcTitle('rc_hail')}
          />
          <MediaGridReport
            company={company}
            items={rcPropertyDamagesPics}
            title={rcTitle('rc_other_property_wind_damage')}
          />
          <MediaGridReport
            company={company}
            items={rcWindDamagesPics}
            title={rcTitle('rc_wind_damages')}
          />
          <MediaGridReport
            company={company}
            items={rcShingleLengthPics}
            title={rcTitle('rc_length_shingle')}
          />
          <MediaGridReport
            company={company}
            items={rcShinglesColorPics}
            title={rcTitle('rc_shingles_color')}
          />
        </>
      )}
    </>
  )
})
